<template>
  <div v-if="columnConfig.isFilterable">
    <v-menu
        open-on-click
        offset-y
        transition="slide-y-transition"
        v-model="isMenuOpen"
        @update:model-value="onMenuToggle"
        content-class="filter-menu"
    >
      <template #activator="{ props }">
        <v-row class="ma-0 mb-2 filter-pill" v-bind="props" @click="props.onClick">
          <v-col class="pill-label">{{ label }}</v-col>
          <v-col class="pill-value px-0">
            <span class="pill-value-inner">
              <v-icon
                  v-if="activeFilters.length > 0"
                  class="pill-clear"
                  icon="fas fa-xmark"
                  @click.stop="clearFilters()"
              />
              <span class="pill-text">{{ getActiveFilters() }}</span>
            </span>
          </v-col>
          <v-col class="pill-icon">
            <v-icon :class="getClass()">
              {{ isMenuOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down' }}
            </v-icon>
          </v-col>
        </v-row>
      </template>

      <v-list v-if="columnConfig.type == 'DateTime'" class="date-filter-picker">
        <v-locale-provider locale="fr">
          <v-date-picker
              @click.stop
              v-model="dateRange"
              multiple="range"
          />
        </v-locale-provider>

        <v-row class="m-0 justify-center align-center pt-2">
          <v-col cols="5" class="pr-0">
            <loader-button
                :label="cancelButton"
                :fullWidth=true
                :outlined="true"
                @callback="onCancel">
            </loader-button>
          </v-col>
          <v-col cols="7">
            <loader-button
                :label="applyButton"
                :fullWidth=true
                @callback="applyFilters">
            </loader-button>
          </v-col>
        </v-row>
      </v-list>
      <v-list v-else>
        <v-item-group class="px-2 filter-list" >
          <search-filter-input
              @click.stop
              variant="outlined"
              placeholder="Rechercher"
              @input-updated="onSearchTextUpdated">
          </search-filter-input>
        </v-item-group>
        <v-item-group class="px-2 filter-list" v-if="!searchText" >
          <div @click.stop>
            <v-checkbox
                label="Tous"
                class="filter-checkbox"
                style="border-bottom: 1px solid #F1F3F4"
                hide-details
                :model-value="allSelected()"
                @update:model-value="(value) => toggleAll(value)"
                >
              <template #label>
                <span class="filter-checkbox-label">Tous</span>
              </template>
            </v-checkbox>
          </div>
        </v-item-group>

        <v-item-group class="px-2 filter-list">
            <div v-for="(item,index) in filteredFilters" :key="index" @click.stop>
              <v-checkbox
                  class="filter-checkbox"
                  :model-value="selectedValue(item)"
                  hide-details
                  @update:model-value="(value) => selectFilter(item, value)">
                <template #label>
                  <span class="filter-checkbox-label">{{ getText(item) + ' (' + item.count + ')' }}</span>
                </template>
              </v-checkbox>
            </div>
        </v-item-group>

        <v-row class="m-0 justify-center align-center pt-2">
          <v-col cols="5" class="pr-0">
            <loader-button
                :label="cancelButton"
                :fullWidth=true
                :outlined="true"
                @callback="onCancel">
            </loader-button>
          </v-col>
          <v-col cols="7">
            <loader-button
                :label="applyButton"
                :fullWidth=true
                @callback="applyFilters">
            </loader-button>
          </v-col>
        </v-row>
      </v-list>
    </v-menu>
  </div>
</template>

<script lang="ts">
import LoaderButton from '@/framework/base/components/LoaderButton.vue';
import {FwkButtons, RessourceFwkButton} from '@/ressources/globalRessources';
import {ColumnConfig, DateRange, ListChoix} from '@/generated/classes/ListData';
import {defineComponent, type PropType} from "vue";
import SearchFilterInput from "@/framework/base/components/SearchFilterInput.vue";

export default defineComponent({
  components: {
    SearchFilterInput,
    LoaderButton
  },
  props: {
    currentFilters: {
      type: Array as PropType<ListChoix[]>,
      required: true
    },
    columnConfig: {
      type: Object as PropType<ColumnConfig>,
      required: true
    },
    headerValue: {
      type: String
    },
    label: {
      type: String
    }
  },
  data() {
    const lastFilter: ListChoix[] = [];
    const activeFilters: ListChoix[] = [];

    return {
      applyButton: RessourceFwkButton(FwkButtons.APPLIQUER).label,
      cancelButton: RessourceFwkButton(FwkButtons.ANNULER).label,
      activeFilters,
      lastFilter,
      isMenuOpen: false,
      searchText: '',
      dateRange: [] as string[],
    };
  },
  watch: {
    currentFilters: {
      handler() {
        this.resetFromCurrentFilters();
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    filters(): ListChoix[] {
      if (JSON.stringify(this.currentFilters) != JSON.stringify(this.lastFilter)) {
        if (this.currentFilters.length == 0 && this.activeFilters.length != 0) {
          this.setFilters(this.currentFilters);
        }
      }
      return this.columnConfig.choix;
    },
    filteredFilters(): ListChoix[] {
      const q = this.searchText.toLowerCase().trim();
      if (!q) return this.filters;

      return this.filters.filter(f => {
        const text = (this.getText(f) ?? '').toLowerCase();
        const val = (String(f.value ?? '')).toLowerCase();
        return text.includes(q) || val.includes(q);
      });
    }
  },
  methods: {
    onSearchTextUpdated(value: string) {
      this.searchText = (value ?? '').trim();
    },
    getActiveFilters(): string {
      if (this.columnConfig.type === 'DateTime') {
        if (this.dateRange?.length) {
          return `${new Date(this.dateRange[0])} - ${new Date(this.dateRange[this.dateRange.length - 1])}`;
        }
        return 'Tous';
      }
      if (this.activeFilters.length == 0) {
        return "Tous"
      } else if (this.activeFilters.length == 1) {
        return this.activeFilters[0].text;
      } else {
        return this.activeFilters.length + " filtres"
      }
    },
    setFilters(value: ListChoix[]) {
      this.lastFilter = value;
      this.activeFilters = value;
    },
    getText(item: ListChoix): string {
      let res = item.text;
      if (item.text == "") {
        res = "Vide";
      }
      return res;
    },
    allSelected(): boolean {
      return this.filters.length > 0 && this.activeFilters.length === this.filters.length;
    },
    toggleAll(value: boolean) {
      if (value) {
        this.activeFilters = [...this.filters];
      } else {
        this.activeFilters = [];
      }
    },
    selectedValue(item: ListChoix): boolean {
      if (this.activeFilters.find(a => item.value == a.value && a.text == item.text)) {
        return true;
      } else {
        return false;
      }
    },
    selectFilter(item: ListChoix, value: boolean) {
      if (event) {
        event.stopPropagation();
      }
      if (value) {
        this.activeFilters.push(item);
      } else {
        const index = this.activeFilters.findIndex(a => item.value == a.value);
        if (index >= 0) {
          this.activeFilters.splice(index, 1);
        }
      }
    },
    toLocalDatePreserved(value: string): Date {
      const localDate = new Date(value);
      return new Date(localDate.getTime() - localDate.getTimezoneOffset() * 60000);
    },
    applyFilters() {
      if (this.columnConfig.type === 'DateTime') {
        if (this.dateRange.length > 0) {
          const sortedDates = [...this.dateRange].sort();
          const startDate = sortedDates[0];
          const endDate = sortedDates[sortedDates.length - 1];
          const dateRange = new DateRange();
          dateRange.dateDebut = new Date(this.toLocalDatePreserved(startDate));
          dateRange.dateFin = new Date(this.toLocalDatePreserved(endDate));

          const dateFilter = new ListChoix();
          dateFilter.dateRange = dateRange;
          this.$emit('apply', [dateFilter], this.headerValue);
        } else {
          this.$emit('apply', [], this.headerValue);
        }
        this.searchText = '';
        return;
      }

      this.$emit('apply', this.activeFilters, this.headerValue);
      this.searchText = '';
    },
    getClass(): string {
      return  'filter-icon-not-active';
    },
    onMenuToggle(value: boolean) {
      this.isMenuOpen = value;
      if (!value) {
        this.resetFromCurrentFilters();
      }
    },
    clearFilters() {
      this.activeFilters = [];
      this.dateRange = [];
      this.applyFilters();
    },
    resetFromCurrentFilters() {
      this.activeFilters = JSON.parse(JSON.stringify(this.currentFilters));
      if (this.columnConfig.type === 'DateTime') {
        const dateFilter = this.currentFilters?.[0];
        if (dateFilter?.dateRange?.dateDebut && dateFilter?.dateRange?.dateFin) {
          this.dateRange = [
            this.toYmdLocal(dateFilter.dateRange.dateDebut),
            this.toYmdLocal(dateFilter.dateRange.dateFin),
          ];
        } else {
          this.dateRange = [];
        }
      }
    },
    toYmdLocal(value: Date | string): string {
      const d = new Date(value);
      const year = d.getUTCFullYear();
      const month = String(d.getUTCMonth() + 1).padStart(2, '0');
      const day = String(d.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    onCancel() {
      this.searchText = '';
      this.resetFromCurrentFilters();
      this.isMenuOpen = false;
    }
  }
})

</script>

<style scoped>
:deep(.filter-checkbox .v-selection-control__input > .v-icon) {
  font-size: 0 !important;
  width: 20px;
  height: 20px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

/* UNCHECKED (default) */
:deep(.filter-checkbox .v-selection-control__input > .v-icon) {
  background-image: url('@/assets/checkbox-unmarked.png');
}

/* CHECKED */
:deep(.filter-checkbox .v-selection-control.v-selection-control--dirty .v-selection-control__input > .v-icon) {
  background-image: url('@/assets/checkbox-marked.png');
}

:deep(.filter-menu) {
  width: 320px;
}

:deep(.filter-menu .v-overlay__content) {
  width: fit-content !important;
  min-width: 0 !important;
  max-width: 90vw;
}
</style>